// components/portal/SourceSetupPanel.tsx
'use client';

import { formatOptions } from '#lib/leasing/formatFit';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { isEntityFocusSubject } from '#lib/leasing/session-focus';
import { resolveSourceAgentMode } from '#lib/leasing/sourceAgentMode';
import { DEFAULT_PROPERTY_SCORING_RUBRIC } from '#types/types';
import { cnccFamilleGroups } from '#lib/leasing/cnccReferential';
import { resolveOpenCampaignScope } from '#lib/leasing/source-view-guards';
import { brandsForCredits, creditsForBrands, resolveSourceBudgetMax } from '#lib/leasing/sourceBudget';
import {
  DEFAULT_SOURCE_COUNTRY_MODE,
  resolveEffectiveCountryCodes,
  SOURCE_COUNTRY_ASSETS_LABELS,
} from '#lib/leasing/source-country-scope';
import type { LeasingPortfolioDTO, LeasingPropertyDTO, LeasingUnitDTO, PortfolioFitCampaignDTO, PropertySourcingCategoryFilters, ScoringCriterion } from '#types/types';
import type { SourceCountryMode } from '#lib/leasing/source-country-scope';
import Switch from '@/components/ui/Switch';
import RangeControl, { SOURCING_BUDGET_MAX } from '@/components/ui/RangeControl';
import { SourceBrandPanel } from './SourceBrandPanel';
import { SourceBulkEnrichPanel } from './SourceBulkEnrichPanel';
import { SourceShortlistPanel, StopRunButton } from './SourceShortlistPanel';
import { ArrowLeft, Check, ChevronDown, Minus, Store, X } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import {
  SOURCING_PRESET_DOCTRINE_KEY,
  parseSourcingPreset,
  type SourcingPresetParams,
  type SourcingPresetParamsSet,
} from '#lib/leasing/sourcingPreset';
import { DoctrineEntryCard, SourceDoctrineModal, defaultDoctrineInstructions, type DoctrineScope } from './SourceDoctrineModal';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';

// Mirrors the native dropdown styling in components/ui/Select.tsx.
const selectStyle: React.CSSProperties = {
  paddingLeft: '8px',
  paddingRight: '8px',
  paddingTop: '4px',
  paddingBottom: '4px',
  borderRadius: '7px',
  backgroundColor: '#262626',
  color: '#cfcfcf',
  width: '100%',
  border: 'none',
  outline: 'none',
};

const useDismissOnOutside = (open: boolean, close: () => void) => {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(close);
  // eslint-disable-next-line react-hooks/refs -- pre-existing ref access through the kernel handle, which is a ref container by design
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) closeRef.current();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return ref;
};

// Which execution engine a launch routes to. NOT a mode, and never a user-facing choice: it is
// derived from what the user selected. Units picked -> unit fit. Assets picked -> asset sourcing.
// Nothing picked -> the whole portfolio. There is only SOURCING; this is an implementation detail.
type SourceSubtool = 'portfolio' | 'asset';

interface SourceSetupPanelProps {
  /** Currently focused portfolio id, or null when the Source editor is empty. */
  selectedPortfolioId: string | null;
  /**
   * The unit the setup was opened from, when the Source subject IS a unit. Not a mode: it only
   * PRESELECTS the scope (its asset in the asset picker, itself in the unit picker). The launch
   * still routes off the selection, exactly like every other campaign.
   */
  focusedUnitId?: string | null;
}


// Native <select> variant: hide the browser arrow and draw a custom chevron at
// a fixed offset from the right edge (the native arrow sat too far right).
const nativeSelectStyle: React.CSSProperties = {
  ...selectStyle,
  paddingRight: '26px',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='16'%20height='16'%20viewBox='0%200%2024%2024'%20fill='none'%20stroke='%23909090'%20stroke-width='2'%20stroke-linecap='round'%20stroke-linejoin='round'%3E%3Cpath%20d='m6%209%206%206%206-6'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  backgroundSize: '14px',
};

// The floating menu surface shared by the four multi-selects below. Hoisted so the object
// identity is stable across renders instead of being minted inline in the JSX.
const dropdownMenuStyle: React.CSSProperties = { backgroundColor: '#353535', borderRadius: '12px' };
/** The composer's rounded chip (the Auto / Image / 2:3 row), for pickers that sit in a composer bar. */
export const PILL_CHIP_CLASS = 'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-full bg-white/[0.06] px-3 text-[12px] leading-none text-white/85 transition-colors hover:bg-white/[0.1] disabled:opacity-50';

const COUNTRY_OPTIONS: { code: string; name: string }[] = [
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'BE', name: 'Belgium' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PT', name: 'Portugal' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'US', name: 'United States' },
];

const resolveCountryName = (code: string, lang: string): string => {
  try {
    return new Intl.DisplayNames([lang === 'fr' ? 'fr' : 'en'], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
};

// Default sourcing parameters (visual pass, local draft only for now).
const DEFAULTS = {
  brandCount: 10,
  // Ten stores: a centre signs proven chains, and a one-unit brand is noise on a shortlist (2026-09-27).
  minimumNetworkSize: 10,
  minimumFitScore: 0,
  // Default ON (owner, 2026-09-27): each run should bring brands the asset has not seen in six
  // months, not the last shortlist again. Turn it off to re-analyse; a filter so narrow that every
  // match was scored recently says so at launch.
  excludeRecentlyScored: true,
  excludeOnsiteTenants: true,
  requireKnownWebsite: false,
  excludeOffScope: true,
};

/**
 * What a NEW campaign on this asset opens with: the asset's saved brief, the defaults where it has
 * none. The launch bar seeds from it too, so it never shows one value and launches another.
 */
export const assetSourcingBrief = (asset: LeasingPropertyDTO | undefined) => {
  const filters = (asset?.sourcingCategoryFilters ?? {}) as PropertySourcingCategoryFilters & { countryCodes?: unknown };
  return {
    countryCodes: Array.isArray(filters.countryCodes)
      ? filters.countryCodes.filter((code): code is string => typeof code === 'string' && code.trim().length > 0)
      : [],
    acceptedCnccActivities: Array.isArray(filters.acceptedCnccActivities) ? [...filters.acceptedCnccActivities] : [],
    surfaceMinSqm: typeof filters.surfaceMinSqm === 'number' ? filters.surfaceMinSqm : 0,
    surfaceMaxSqm: typeof filters.surfaceMaxSqm === 'number' ? filters.surfaceMaxSqm : 0,
    acceptedFormats: Array.isArray(filters.acceptedFormats) ? [...filters.acceptedFormats] : [],
    minimumNetworkSize: typeof filters.minimumNetworkSize === 'number' && Number.isFinite(filters.minimumNetworkSize)
      ? Math.max(1, Math.floor(filters.minimumNetworkSize))
      : DEFAULTS.minimumNetworkSize,
    excludeRecentlyScored: typeof asset?.sourcingExclusionRecentLeads === 'boolean' ? asset.sourcingExclusionRecentLeads : DEFAULTS.excludeRecentlyScored,
    excludeOnsiteTenants: typeof asset?.sourcingExclusionTenants === 'boolean' ? asset.sourcingExclusionTenants : DEFAULTS.excludeOnsiteTenants,
    requireKnownWebsite: filters.requireKnownWebsite === true,
    excludeOffScope: filters.excludeOffScope !== false,
  };
};

/**
 * Dismiss a floating dropdown on an outside pointer-down or the Escape key.
 * Returns a ref to spread on the dropdown's wrapper element; anything inside it
 * (trigger + menu) counts as "inside" and won't dismiss. Only re-subscribes when
 * `open` flips, and reads the latest `close` via a ref to avoid listener churn.
 */

/**
 * One selectable row of the multi-selects below. Memoised and handed its own id, so a re-render of
 * the panel (a budget drag, an inventory tick) does not repaint the ~150 CNCC sector rows: the row
 * calls `onSelect(id)` itself instead of receiving a fresh closure per render.
 */
const MultiSelectRow = React.memo(({
  id,
  label,
  code,
  state,
  className,
  iconSize,
  trailing,
  onSelect,
}: {
  id: string;
  label: string;
  /** Fixed-width code column, used by the country picker only. */
  code?: string;
  state: 'off' | 'on' | 'partial';
  className: string;
  iconSize: number;
  trailing?: React.ReactNode;
  onSelect: (id: string) => void;
}) => {
  const handleClick = useCallback(() => onSelect(id), [id, onSelect]);
  return (
    <button type="button" onClick={handleClick} className={className}>
      {code ? <span className="w-7 shrink-0 font-mono text-[11px] text-white/45">{code}</span> : null}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {trailing}
      {state === 'on' ? (
        <Check size={iconSize} className="shrink-0 text-white/80" />
      ) : state === 'partial' ? (
        <Minus size={iconSize} className="shrink-0 text-white/50" />
      ) : null}
    </button>
  );
});

/** Compact Studio-style country scope picker. */
export const CountryMultiSelect = React.memo(({
  mode,
  value,
  onChange,
  onModeChange,
  effectiveCountryCodes,
  lang,
}: {
  mode: SourceCountryMode;
  value: string[];
  onChange: (next: string[]) => void;
  onModeChange: (mode: SourceCountryMode) => void;
  effectiveCountryCodes: string[];
  lang: string;
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const toggleOpen = useCallback(() => setOpen((o) => !o), []);
  const containerRef = useDismissOnOutside(open, close);

  const summary = mode === 'ASSETS'
    ? (lang === 'fr' ? SOURCE_COUNTRY_ASSETS_LABELS.fr : SOURCE_COUNTRY_ASSETS_LABELS.en)
    : value.length === 0
      ? (lang === 'fr' ? 'Tous les pays' : 'All countries')
      : value.join(', ');
  const derivedCountryCodes = mode === 'ASSETS' ? effectiveCountryCodes : [];
  const derivedCountryLabel = lang === 'fr'
    ? 'Pays déduit des assets sélectionnés'
    : 'Country derived from selected assets';
  const countryOptions = useMemo(() => {
    const knownCodes = new Set(COUNTRY_OPTIONS.map((country) => country.code));
    const extraCodes = Array.from(new Set([...effectiveCountryCodes, ...value]))
      .filter((code) => !knownCodes.has(code))
      .map((code) => ({ code, name: resolveCountryName(code, lang) }));
    return [...COUNTRY_OPTIONS, ...extraCodes];
  }, [effectiveCountryCodes, lang, value]);

  const toggle = useCallback((code: string) => {
    const set = new Set(value);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    onModeChange('MANUAL');
    onChange(Array.from(set));
  }, [value, onChange, onModeChange]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        style={selectStyle}
        className="flex items-center justify-between gap-2 text-left text-sm"
      >
        <span className={'truncate ' + (mode === 'ASSETS' || value.length ? 'text-[#cfcfcf]' : 'text-[#909090]')}>{summary}</span>
        <ChevronDown size={16} className={'shrink-0 text-[#909090] transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open ? (
        <div
          className="custom-scrollbar absolute z-10 mt-1 max-h-[220px] w-full overflow-y-auto p-1"
          style={dropdownMenuStyle}
        >
          <button
            type="button"
            onClick={() => { onModeChange('ASSETS'); setOpen(false); }}
            className={'mb-1 flex w-full items-center rounded-[5px] px-2 py-1.5 text-left text-[13px] transition-colors ' +
              (mode === 'ASSETS' ? 'bg-white/[0.08] text-white/90' : 'text-white/70 hover:bg-white/[0.05]')}
          >
            <span className="min-w-0 flex-1 truncate">
              {lang === 'fr' ? SOURCE_COUNTRY_ASSETS_LABELS.fr : SOURCE_COUNTRY_ASSETS_LABELS.en}
            </span>
            {mode === 'ASSETS' ? <Check size={14} className="shrink-0 text-white/80" /> : null}
          </button>
          {countryOptions.map((country) => {
            const active = mode === 'MANUAL' && value.includes(country.code);
            return (
              <MultiSelectRow
                key={country.code}
                id={country.code}
                code={country.code}
                label={country.name}
                state={active ? 'on' : 'off'}
                iconSize={14}
                trailing={derivedCountryCodes.includes(country.code) ? (
                  <span
                    role="img"
                    aria-label={derivedCountryLabel}
                    title={derivedCountryLabel}
                    className="shrink-0 text-white/45"
                  >
                    <Store size={13} aria-hidden="true" />
                  </span>
                ) : null}
                onSelect={toggle}
                className={
                  'flex w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-[13px] transition-colors ' +
                  (active ? 'bg-white/[0.08] text-white/90' : 'text-white/70 hover:bg-white/[0.05]')
                }
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
});

/**
 * Compact Studio-style multi-select over an id/label list, built on the same shell as the country
 * picker above. Two callers: the asset scope and the unit scope of the setup form.
 */
export const IdMultiSelect = React.memo(({
  options,
  value,
  onChange,
  emptyLabel,
  disabled,
  direction = 'down',
  variant = 'field',
}: {
  options: { id: string; label: string }[];
  value: string[];
  onChange: (next: string[]) => void;
  /** What "nothing selected" means, spelled out rather than left blank. */
  emptyLabel: string;
  disabled?: boolean;
  /** 'up' for a picker near the bottom of the screen (the dashboard's Launch bar). */
  direction?: 'down' | 'up';
  /** 'pill': the composer's rounded chip instead of the form's full-width field. */
  variant?: 'field' | 'pill';
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const toggleOpen = useCallback(() => setOpen((o) => !o), []);
  const containerRef = useDismissOnOutside(open, close);

  const selected = useMemo(() => new Set(value), [value]);
  const summary =
    value.length === 0
      ? emptyLabel
      : value.length === 1
        ? options.find((option) => option.id === value[0])?.label || value[0]
        : `${value.length}`;

  const toggle = useCallback((id: string) => {
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    // Keep the picker's own order so the launch reads them the way the list shows them.
    onChange(options.filter((option) => set.has(option.id)).map((option) => option.id));
  }, [selected, options, onChange]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        style={variant === 'pill' ? undefined : selectStyle}
        className={variant === 'pill' ? PILL_CHIP_CLASS + ' max-w-[240px]' : 'flex items-center justify-between gap-2 text-left text-sm disabled:opacity-50'}
      >
        <span className={'truncate ' + (variant === 'pill' ? '' : value.length ? 'text-[#cfcfcf]' : 'text-[#909090]')}>{summary}</span>
        <ChevronDown size={variant === 'pill' ? 14 : 16} className={'shrink-0 text-[#909090] transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open ? (
        <div
          className={'custom-scrollbar absolute z-10 max-h-[260px] overflow-y-auto p-1 ' + (variant === 'pill' ? 'w-[240px] ' : 'w-full ') + (direction === 'up' ? 'bottom-full mb-1' : 'mt-1')}
          style={dropdownMenuStyle}
        >
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-[12px] text-white/40">{emptyLabel}</div>
          ) : (
            options.map((option) => {
              const active = selected.has(option.id);
              return (
                <MultiSelectRow
                  key={option.id}
                  id={option.id}
                  label={option.label}
                  state={active ? 'on' : 'off'}
                  iconSize={14}
                  onSelect={toggle}
                  className={
                    'flex w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-[13px] transition-colors ' +
                    (active ? 'bg-white/[0.08] text-white/90' : 'text-white/70 hover:bg-white/[0.05]')
                  }
                />
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
});

/**
 * Grouped CNCC picker: familles (A-G) as headers, their precise secteurs as selectable
 * rows (2026-07-16 call: "focus-toi sur toutes les marques de basket" needs the secteur,
 * not just the broad famille). `value` is the list of selected secteur labels. Clicking a
 * famille header selects/deselects ALL its secteurs.
 */
export const CnccMultiSelect = React.memo(({
  value,
  onChange,
  lang,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  lang: string;
}) => {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const toggleOpen = useCallback(() => setOpen((o) => !o), []);
  const containerRef = useDismissOnOutside(open, close);
  const groups = useMemo(() => cnccFamilleGroups(), []);

  const selected = useMemo(() => new Set(value), [value]);
  const summary =
    value.length === 0
      ? lang === 'fr'
        ? 'Toutes les catégories'
        : 'All categories'
      : value.length === 1
        ? value[0]
        : `${value.length} ${lang === 'fr' ? 'secteurs' : 'sectors'}`;

  const setNext = useCallback((set: Set<string>) => onChange(Array.from(set).sort()), [onChange]);
  const toggleSecteur = useCallback((secteur: string) => {
    const set = new Set(selected);
    if (set.has(secteur)) set.delete(secteur);
    else set.add(secteur);
    setNext(set);
  }, [selected, setNext]);
  // Takes the famille's sector code rather than its secteur list: the memoised header row hands
  // back its own id, and the list is resolved from `groups` here.
  const toggleFamille = useCallback((sector: string) => {
    const secteurs = groups.find((group) => group.sector === sector)?.secteurs;
    if (!secteurs) return;
    const allOn = secteurs.every((s) => selected.has(s));
    const set = new Set(selected);
    for (const s of secteurs) { if (allOn) set.delete(s); else set.add(s); }
    setNext(set);
  }, [groups, selected, setNext]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        style={selectStyle}
        className="flex items-center justify-between gap-2 text-left text-sm"
      >
        <span className={'truncate ' + (value.length ? 'text-[#cfcfcf]' : 'text-[#909090]')}>{summary}</span>
        <ChevronDown size={16} className={'shrink-0 text-[#909090] transition-transform ' + (open ? 'rotate-180' : '')} />
      </button>
      {open ? (
        <div
          className="custom-scrollbar absolute z-10 mt-1 max-h-[300px] w-full overflow-y-auto p-1"
          style={dropdownMenuStyle}
        >
          {groups.map((group) => {
            const allOn = group.secteurs.every((s) => selected.has(s));
            const someOn = group.secteurs.some((s) => selected.has(s));
            return (
              <div key={group.sector} className="mb-0.5">
                <MultiSelectRow
                  id={group.sector}
                  label={`${group.famille} (${group.sector})`}
                  state={allOn ? 'on' : someOn ? 'partial' : 'off'}
                  iconSize={13}
                  onSelect={toggleFamille}
                  className={
                    'flex w-full items-center gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] font-medium transition-colors ' +
                    (allOn ? 'text-white/90' : someOn ? 'text-white/75' : 'text-white/55 hover:bg-white/[0.05]')
                  }
                />
                {group.secteurs.map((secteur) => {
                  const active = selected.has(secteur);
                  return (
                    <MultiSelectRow
                      key={secteur}
                      id={secteur}
                      label={secteur}
                      state={active ? 'on' : 'off'}
                      iconSize={13}
                      onSelect={toggleSecteur}
                      className={
                        'flex w-full items-center gap-2 rounded-[5px] py-1 pl-5 pr-2 text-left text-[12px] transition-colors ' +
                        (active ? 'bg-white/[0.08] text-white/90' : 'text-white/65 hover:bg-white/[0.05]')
                      }
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
});

/**
 * Right-bar setup for the Source editor, styled like a Studio control panel.
 * The portfolio dropdown is always active; every other parameter is shown from
 * the start but disabled until a portfolio is selected.
 *
 * NOTE: parameter values are local draft state for now (visual pass). Persisting
 * drafts + launching the campaign will be wired to the backend next.
 */
export const SourceSetupPanel = ({ selectedPortfolioId, focusedUnitId }: SourceSetupPanelProps) => {
  const { kernel } = useKernel();
  const activeSessionId = useKernelStore((state) => state.activeChatSessionId);
  const portfolioRegistry = useKernelStore((state) => state.inventory.Portfolio.registry);
  const lang = useKernelStore((state) => state.language);
  const sourceFocusedFitId = useKernelStore((state) => state.sourceFocusedFitId);
  const sourceSelectedFitIds = useKernelStore((state) => state.sourceSelectedFitIds);
  const propertyRegistry = useKernelStore((state) => state.inventory.Property.registry);
  const unitRegistry = useKernelStore((state) => state.inventory.Unit.registry);
  const sourceCreatingCampaign = useKernelStore((state) => state.sourceCreatingCampaign);
  const sourceHeaderIsDashboard = useKernelStore((state) => state.sourceHeaderIsDashboard);
  const sourceSelectedCampaignId = useKernelStore((state) => state.sourceSelectedCampaignId);
  // What the OPEN campaign covered, read off its own row. Never a mode: it decides which
  // shortlist panel the right bar shows, nothing else.
  const openCampaignScope = useKernelStore(resolveOpenCampaignScope);
  // Open campaign status: while the run is ACTIVE the right bar shows a live "review in progress"
  // panel instead of the shortlist tools (which would misleadingly count still-hidden rows, invite
  // enrichment mid-run, and offer a re-score the server refuses with a 409 until the run lands).
  const openCampaignActive = useKernelStore((state) => {
    const id = state.sourceSelectedCampaignId;
    if (!id) return false;
    const campaign = readCampaignRow(state.inventory, id);
    if (!campaign) return false;
    return (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(String(campaign.status ?? '').toUpperCase());
  });

  const [ids, setIds] = useState<string[]>([]);

  // THE SCOPE OF A LAUNCH, and the only thing the user picks: one or more assets.
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

  // Assets the user worked on recently, surfaced at the top of the picker so launching the
  // next campaign on the same centre is one click, not a scroll through the whole portfolio
  // (Altarea feedback, 2026-07-24). Recency is personal but not per-browser: it lives in
  // User.preferences, so the same leasing manager gets their centres on any machine.
  // Undefined until preferences hydrate, the picker is then simply alphabetical.
  const recentAssetIds = useKernelStore((state) => state.userPreferences.sourceRecentAssetIds);
  const [launching, setLaunching] = useState(false);

  const subtool: SourceSubtool = selectedAssetIds.length > 0 ? 'asset' : 'portfolio';
  // The ONE asset the per-asset machinery can talk about: seeding filters from the asset's stored
  // config, the live eligible-brand count, the rubric onboarding, the unit list. With several
  // assets selected there is no single rubric to reason about, so those all stand down.
  const selectedAssetId = selectedAssetIds.length === 1 ? (selectedAssetIds[0] ?? '') : '';

  // The asset a focused unit belongs to, i.e. the scope the form opens on. Read off the registry
  // rather than passed in: the caller knows the unit, the unit knows its asset.
  const focusedUnitAssetId = focusedUnitId
    ? ((unitRegistry[focusedUnitId] as LeasingUnitDTO | undefined)?.propertyId ?? '')
    : '';

  // THE ASSET THE CONVERSATION IS ABOUT.
  //
  // A focused UNIT already narrowed this form onto its asset; a focused ASSET did not, so asking
  // the agent to "set up Aubergenville for asset-level sourcing" wrote every filter onto the
  // property correctly and left the user looking at the PORTFOLIO scope, where none of it shows.
  // Same precedent, one more subject type.
  const focusedAssetId = useKernelStore((state) => {
    const sessionId = state.activeChatSessionId;
    const subject = sessionId ? state.inventory.ChatSession.registry[sessionId]?.activeSubject : null;
    return isEntityFocusSubject(subject) && subject.entityType === 'Property' ? subject.entityId : '';
  });

  const [countryCodes, setCountryCodes] = useState<string[]>([]);
  const [countryMode, setCountryMode] = useState<SourceCountryMode>(DEFAULT_SOURCE_COUNTRY_MODE);
  const effectiveCountryCodes = useMemo(
    () => resolveEffectiveCountryCodes(
      countryMode,
      countryCodes,
      selectedAssetIds
        .map((id) => propertyRegistry[id] as LeasingPropertyDTO | undefined)
        .filter((property): property is LeasingPropertyDTO => Boolean(property)),
    ),
    [countryCodes, countryMode, propertyRegistry, selectedAssetIds],
  );
  const handleCountryModeChange = useCallback((mode: SourceCountryMode) => {
    setCountryMode(mode);
  }, []);
  const handleCountryCodesChange = useCallback((next: string[]) => {
    setCountryCodes(next);
    setCountryMode('MANUAL');
  }, []);
  // WHAT THE USER PICKS IS A NUMBER OF BRANDS (owner, 2026-09-27: "I launched a campaign for 10
  // brands and it only got 2"): the credits are what those brands cost, research and pick
  // included, and are derived, never typed.
  const [brandCount, setBrandCount] = useState(DEFAULTS.brandCount);
  const brandsFromBudget = (credits: number) => Math.max(1, brandsForCredits(credits, true));
  const [minimumNetworkSize, setMinimumNetworkSize] = useState(DEFAULTS.minimumNetworkSize);
  const [minimumFitScore, setMinimumFitScore] = useState(DEFAULTS.minimumFitScore);
  const [excludeRecentlyScored, setExcludeRecentlyScored] = useState(DEFAULTS.excludeRecentlyScored);
  const [excludeOnsiteTenants, setExcludeOnsiteTenants] = useState(DEFAULTS.excludeOnsiteTenants);
  const [requireKnownWebsite, setRequireKnownWebsite] = useState(DEFAULTS.requireKnownWebsite);
  const [excludeOffScope, setExcludeOffScope] = useState(DEFAULTS.excludeOffScope);
  // Research runs on every pass; this only decides whether fields checked in the last 30 days are kept.
  const [useLegacyData, setUseLegacyData] = useState(true);
  const toggleUseLegacyData = useCallback((on: boolean) => setUseLegacyData(on), []);
  const [cnccActivities, setCnccActivities] = useState<string[]>([]);
  // Target unit surface range in m2 (0 = no bound). Brands whose declared surface range
  // definitely cannot fit are excluded server-side; unknown-surface brands are kept.
  const [surfaceMinSqm, setSurfaceMinSqm] = useState(0);
  const [surfaceMaxSqm, setSurfaceMaxSqm] = useState(0);
  const [formats, setFormats] = useState<string[]>([]);
  // Live count of brands eligible under the current asset filters, drives the budget slider's max
  // and the eligible-count estimate so the scope reacts to the user's filters (incl. country) before
  // launch. null = not yet computed / not applicable (portfolio & unit scopes).
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);
  const [eligibleCountLoading, setEligibleCountLoading] = useState(false);
  // Sourcing doctrine. The scoring grid is the ".csv" file, read-only (one grid for every run); the
  // agent instructions are the ".md". Here they are a genuine PRE-LAUNCH draft, not a mock: a shortlist's instructions are a
  // DoctrineDocument keyed on the campaign id, and no campaign exists yet. The asset and unit
  // drafts are written to `campaign:<id>` the moment the launch returns one (see handleLaunch);
  // from then on the asset shortlist's right bar reads and writes that document directly. A
  // portfolio run is a PortfolioFitCampaign, which the CAMPAIGN doctrine scope does not resolve,
  // so its draft stays local until it does.
  const [doctrineOpen, setDoctrineOpen] = useState(false);
  const [instructionsByScope, setInstructionsByScope] = useState<Record<DoctrineScope, string>>(() => ({
    portfolio: defaultDoctrineInstructions('portfolio', lang),
    asset: defaultDoctrineInstructions('asset', lang),
  }));

  // THE SAVED PARAMETERS OF THE SELECTED PORTFOLIO, READ REACTIVELY.
  //
  // The form below reads the portfolio through getState() - deliberately, so a background refresh
  // cannot overwrite what the user is typing. The cost was that it never noticed a real change
  // either: asked to "set up the params, I will validate before launching", the agent wrote
  // France and a 2-store minimum onto the portfolio, said so, and the form went on showing "All
  // countries" and "1". The user was told one thing and shown another, which reads as the agent
  // being unable to do it at all.
  //
  // A signature string (not an object) so the selector is referentially stable, and only a real
  // change to these two saved values re-seeds the form. A user edit does not touch the record, so
  // their in-progress work still survives every background refresh.
  // The asset's saved brief, read REACTIVELY for the same reason as the portfolio's below: the
  // form reads records through getState() so a background refresh cannot overwrite typing, which
  // also meant it never noticed the agent saving a brief on the user's behalf.
  const assetSourcingSignature = useKernelStore((state) => {
    // focusedAssetId ONLY. Reading selectedAssetIds here made this signature change whenever the
    // seeding effect set the selection, and that effect depends on this signature: it fed itself,
    // and the panel re-rendered without end. The agent saw it before I did ("the sourcing setup
    // panel hit a refresh loop while updating") and routed around the panel to get its work done.
    const assetId = focusedAssetId;
    const property = assetId
      ? (state.inventory.Property.registry[assetId] as LeasingPropertyDTO | undefined)
      : undefined;
    return JSON.stringify([
      property?.sourcingCategoryFilters ?? null,
      property?.sourcingExclusionTenants ?? null,
      property?.sourcingExclusionRecentLeads ?? null,
      (property as { runtime?: { sourcingBudgetCreditsDraft?: number } } | undefined)?.runtime?.sourcingBudgetCreditsDraft ?? null,
    ]);
  });


  const disabled = !selectedPortfolioId;

  // Debounced live eligible-brand count for the ASSET scope. Recomputes whenever any filter that
  // affects the candidate pool changes (asset, country, CNCC, surface, min-score,
  // exclusions), this is what makes selecting e.g. "Italy" immediately re-cap the budget.
  useEffect(() => {
    if (subtool !== 'asset' || !selectedAssetId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      setEligibleCount(null);
      setEligibleCountLoading(false);
      return;
    }
    let cancelled = false;
    setEligibleCountLoading(true);
    const handle = setTimeout(() => {
      void fetch(`/api/inventory/properties/${encodeURIComponent(selectedAssetId)}/sourcing-count`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operatingCountryCode: countryCodes[0] ?? null,
          acceptedCnccActivities: cnccActivities,
          surfaceMinSqm,
          surfaceMaxSqm,
          portfolioFitMinimumScore: minimumFitScore,
          minimumNetworkSize,
          requireKnownWebsite,
          excludeOffScope,
          excludePropertyBrands: excludeOnsiteTenants,
          excludeRecentLeads: excludeRecentlyScored,
        }),
      })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((json) => {
          if (!cancelled) setEligibleCount(typeof json?.count === 'number' ? json.count : null);
        })
        .catch(() => {
          if (!cancelled) setEligibleCount(null);
        })
        .finally(() => {
          if (!cancelled) setEligibleCountLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [
    subtool,
    selectedAssetId,
    countryCodes,
    cnccActivities,
    surfaceMinSqm,
    surfaceMaxSqm,
    minimumFitScore,
    minimumNetworkSize,
    requireKnownWebsite,
    excludeOffScope,
    excludeOnsiteTenants,
    excludeRecentlyScored,
  ]);

  // Allow headroom above the current eligible pool: the observed analysis cost varies by brand.
  // Unknown counts keep the global ceiling.
  const budgetMax = resolveSourceBudgetMax(SOURCING_BUDGET_MAX, subtool, eligibleCount);

  const brandMax = Math.max(1, brandsForCredits(budgetMax, true));
  const budgetCredits = creditsForBrands(brandCount * Math.max(1, selectedAssetIds.length), true);

  // Keep the chosen count within the (possibly shrunk) max.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
    setBrandCount((prev) => (prev > brandMax ? brandMax : prev));
  }, [brandMax]);

  // Seed the draft filters from the selected asset's persisted sourcing config so a
  // relaunch from this panel never silently clears filters set earlier (panel or agent).
  // Detail-sync the chosen asset so sourcingCategoryFilters is actually present: the picker
  // rows come from a list query, and seeding from a row that has not been detailed yet reads
  // undefined filters, which the launch below then PERSISTS over the real ones.
  useEffect(() => {
    const k = kernel.current;
    if (!k || !selectedAssetId) return;
    void k.syncEntity('Property', selectedAssetId);
  }, [kernel, selectedAssetId]);

  const selectedAssetRecord = selectedAssetId
    ? (propertyRegistry[selectedAssetId] as LeasingPropertyDTO | undefined)
    : undefined;

  useEffect(() => {
    const k = kernel.current;
    if (!k || !selectedAssetId) return;
    const property = selectedAssetRecord;
    if (!property) return;
    const stored = property.sourcingCategoryFilters;
    if (stored && typeof stored === 'object') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing it alters when state settles
      if (typeof stored.portfolioFitMinimumScore === 'number') setMinimumFitScore(stored.portfolioFitMinimumScore);
      if (Array.isArray(stored.acceptedCnccActivities)) setCnccActivities([...stored.acceptedCnccActivities]);
      if (typeof stored.surfaceMinSqm === 'number') setSurfaceMinSqm(stored.surfaceMinSqm);
      if (typeof stored.surfaceMaxSqm === 'number') setSurfaceMaxSqm(stored.surfaceMaxSqm);
      if (Array.isArray(stored.acceptedFormats)) setFormats([...stored.acceptedFormats]);
    }
    if (typeof property.sourcingExclusionTenants === 'boolean') setExcludeOnsiteTenants(property.sourcingExclusionTenants);
    // NOTE: intentionally do NOT seed "exclude recently scored" from the property's persisted value.
    // Historically it defaulted ON and got persisted on ~every property, which made re-launches
    // dead-end with "aucune nouvelle marque" (all matching brands already scored). The toggle now
    // always starts from the re-analysis-first default (OFF); launching overwrites the stale
    // persisted value, so it self-heals without a bulk data migration.
    // Depends on the RECORD, not just the id: the row arrives asynchronously and this effect
    // used to run once, before it landed, and then never again.
  }, [kernel, selectedAssetId, selectedAssetRecord]);

  // No automatic agent turns on this form (owner, 2026-09-26): opening a draft, picking an asset,
  // moving a filter or launching never wakes the agent. It speaks when asked, and when a run lands.

  useEffect(() => {
    const k = kernel.current;
    if (!k) return;
    let cancelled = false;
    void (async () => {
      const result = await k.queryInventory('Portfolio', {
        filters: { minAssets: 1, sortBy: 'ASSETS', sortDir: 'desc' },
        limit: 50,
        offset: 0,
        force: true,
      });
      if (cancelled) return;
      setIds(Array.isArray(result?.ids) ? result.ids : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [kernel]);

  // Populate the right-bar form. When a campaign is open, mirror ITS params so
  // the user can see exactly what it was launched with (and relaunch/reapply via
  // the Launch button); otherwise (new campaign) fall back to defaults. Read the
  // campaign/portfolio non-reactively (getState) so background live-refresh
  // updates don't clobber the values while the form is shown.
  useEffect(() => {
    const state = kernel.current?.getState();
    // Fields that aren't part of a portfolio-fit campaign always reset. A setup opened FROM a unit
    // starts already narrowed onto it: its asset is the scope, the unit is in the unit picker.
    // Same form, same filters, one less thing to click.
    // The scope the form opens on: the focused unit's asset, else the focused asset itself.
     
    const openingAssetId = focusedUnitAssetId || focusedAssetId;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change, not merge work
    setSelectedAssetIds(openingAssetId ? [openingAssetId] : []);

    // THE ASSET'S SAVED BRIEF IS WHAT THE FORM SHOWS, NOT THE DEFAULTS.
    //
    // These five were hard-reset on every seed, so an asset brief - whoever set it, the user last
    // week or the agent thirty seconds ago - was invisible here. Asked to "apply the Aubergenville
    // asset-level sourcing params", the agent wrote all of them onto the property, said so line by
    // line, and the panel went on showing all countries / 0 / off: the user was told one thing
    // and shown another, which is indistinguishable from the agent having failed.
    const openingAsset = openingAssetId && state
      ? (state.inventory.Property.registry[openingAssetId] as LeasingPropertyDTO | undefined)
      : undefined;
    const assetFilters = (openingAsset?.sourcingCategoryFilters ?? {}) as PropertySourcingCategoryFilters;
    const brief = assetSourcingBrief(openingAsset);
    setMinimumFitScore(
      typeof assetFilters.portfolioFitMinimumScore === 'number'
        ? assetFilters.portfolioFitMinimumScore
        : DEFAULTS.minimumFitScore
    );
    setCnccActivities(brief.acceptedCnccActivities);
    setSurfaceMinSqm(brief.surfaceMinSqm);
    setSurfaceMaxSqm(brief.surfaceMaxSqm);
    setFormats(brief.acceptedFormats);
    setExcludeOnsiteTenants(brief.excludeOnsiteTenants);

    // AN OPEN CAMPAIGN IS READ FROM `Campaign`, the table it is actually written to.
    //
    // This block used to read `PortfolioFitCampaign.registry`, a registry for a table that holds
    // ZERO rows since the campaign families were unified. Every lookup missed, so opening any
    // campaign silently reset the countries, the budget, the exclusions, the website requirement
    // and the network floor to their defaults - which is why parameters "never stuck". The keys
    // moved with the table: a run's country is `countryFilter`, its exclusions are
    // `excludeRecentLeads` / `excludePropertyBrands`, and the two brand-level settings live on the
    // asset's own filters, which are seeded above.
    const openConfig =
      sourceSelectedCampaignId && state
        ? ((state.inventory.Campaign.registry[sourceSelectedCampaignId] as { config?: Record<string, unknown> } | undefined)?.config)
        : undefined;

    if (openConfig) {
      const country = typeof openConfig.countryFilter === 'string' ? openConfig.countryFilter.trim() : '';
      setCountryCodes(country.length > 0 ? [country] : []);
      setCountryMode(country.length > 0 ? 'MANUAL' : 'ASSETS');
      setBrandCount(
        typeof openConfig.maxBudgetCredits === 'number' && Number.isFinite(openConfig.maxBudgetCredits)
          ? brandsFromBudget(openConfig.maxBudgetCredits)
          : DEFAULTS.brandCount,
      );
      setExcludeRecentlyScored(openConfig.excludeRecentLeads !== false);
      setRequireKnownWebsite(brief.requireKnownWebsite);
      setExcludeOffScope(assetFilters.excludeOffScope !== false);
      setMinimumNetworkSize(brief.minimumNetworkSize);
      return;
    }

    // New campaign / dashboard → the portfolio's own saved parameters where it has them, defaults
    // otherwise. Countries were hard-reset to "all" here, so the one field the agent is most often
    // asked to set ("only France") could never show up in the form it was set for.
    // The asset's budget lives in its runtime draft (there is no column for it), which is where
    // LAUNCH_SOURCING_CAMPAIGN already puts it. Reading it back is what makes "set the budget to
    // 10" show up as 10 instead of the form's default.
    const assetBudgetDraft =
      (openingAsset as { sourcingBudgetCreditsDraft?: number; runtime?: { sourcingBudgetCreditsDraft?: number } } | undefined)
        ?.sourcingBudgetCreditsDraft
      ?? (openingAsset as { runtime?: { sourcingBudgetCreditsDraft?: number } } | undefined)?.runtime?.sourcingBudgetCreditsDraft;
    setBrandCount(
      typeof assetBudgetDraft === 'number' && Number.isFinite(assetBudgetDraft) && assetBudgetDraft > 0
        ? brandsFromBudget(assetBudgetDraft)
        : DEFAULTS.brandCount
    );
    setExcludeRecentlyScored(brief.excludeRecentlyScored);
    // THE ASSET IS WHERE THE BRIEF WRITES, SO THE ASSET IS WHAT WE READ BACK. These two seeded from
    // `Portfolio.portfolioFitCountryCodes` and `Portfolio.portfolioFitMinimumNetworkSize`, columns
    // of the retired macro pass that nothing writes any more: the user set five stores minimum, it
    // was saved on the asset, and the form came back showing one. A setting that does not survive
    // reopening the form reads exactly like a setting that was never applied.
    setRequireKnownWebsite(brief.requireKnownWebsite);
    setExcludeOffScope(assetFilters.excludeOffScope !== false);
    setCountryCodes(brief.countryCodes);
    setCountryMode(brief.countryCodes.length > 0 ? 'MANUAL' : 'ASSETS');
    setMinimumNetworkSize(brief.minimumNetworkSize);
    // assetSourcingSignature is what makes an agent's write appear: without it this effect only
    // re-ran when the user switched asset or campaign, so params set on the CURRENT one landed in
    // the database and nowhere else. It watched the PORTFOLIO's retired columns until 2026-08-18,
    // which is a change detector on a value nothing writes.
  }, [selectedPortfolioId, sourceSelectedCampaignId, kernel, focusedUnitId, focusedUnitAssetId, focusedAssetId, assetSourcingSignature]);

  const portfolios = useMemo(
    () =>
      ids
        .map((id) => portfolioRegistry[id] as LeasingPortfolioDTO | undefined)
        .filter((portfolio): portfolio is LeasingPortfolioDTO => Boolean(portfolio)),
    [ids, portfolioRegistry]
  );

  // Real assets (properties) for the selected portfolio, replaces the mock list.
  const selectedPortfolio = selectedPortfolioId
    ? (portfolioRegistry[selectedPortfolioId] as LeasingPortfolioDTO | undefined)
    : undefined;

  // Detail-sync the selected portfolio so its propertyIds are populated.
  useEffect(() => {
    const k = kernel.current;
    if (!k || !selectedPortfolioId) return;
    void k.syncEntity('Portfolio', selectedPortfolioId);
  }, [kernel, selectedPortfolioId]);

  const assetIds = useMemo(() => {
    const raw = Array.isArray(selectedPortfolio?.propertyIds) ? selectedPortfolio.propertyIds : [];
    return Array.from(new Set(raw.filter((id): id is string => typeof id === 'string' && id.length > 0)));
  }, [selectedPortfolio?.propertyIds]);

  useEffect(() => {
    const k = kernel.current;
    if (!k || assetIds.length === 0) return;
    const missing = assetIds.filter((id) => !propertyRegistry[id]);
    if (missing.length === 0) return;
    void k.ensureInventoryIds('Property', missing, { force: true });
  }, [kernel, assetIds, propertyRegistry]);

  const assets = useMemo(
    () =>
      assetIds
        .map((id) => propertyRegistry[id] as LeasingPropertyDTO | undefined)
        .filter((property): property is LeasingPropertyDTO => Boolean(property))
        // Alphabetical, so a leasing manager scanning for their own centre finds it by name
        // (Altarea feedback, 2026-07-24). locale 'fr' keeps accented names in the right place.
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })),
    [assetIds, propertyRegistry]
  );



  // Recently launched assets float to the top, the rest stay alphabetical, so launching the next
  // campaign on the same centre is one click and not a scroll through the whole portfolio
  // (Altarea feedback, 2026-07-24).
  const assetOptions = useMemo(() => {
    const byId = new Map(assets.map((asset) => [asset.id, asset]));
    // Optional since the merge: recents moved from component state to a user preference, which is
    // absent until the user has launched once.
    const recent = (recentAssetIds ?? [])
      .map((id) => byId.get(id))
      .filter((asset): asset is LeasingPropertyDTO => Boolean(asset))
      .slice(0, 3);
    const recentIds = new Set(recent.map((asset) => asset.id));
    return [...recent, ...assets.filter((asset) => !recentIds.has(asset.id))].map((asset) => ({
      id: asset.id,
      label: asset.name || asset.id,
    }));
  }, [assets, recentAssetIds]);

  const handleSelect = useCallback(
    (portfolioId: string) => {
      const k = kernel.current;
      const sessionId = k?.getActiveChatSessionId() ?? activeSessionId;
      const normalized = typeof portfolioId === 'string' ? portfolioId.trim() : '';
      if (!k || !sessionId || !normalized) return;

      k.setActiveSubject(sessionId, {
        entityType: 'Portfolio',
        entityId: normalized,
        focusActionType: 'ORCHESTRATE_SOURCING',
      });
    },
    [activeSessionId, kernel]
  );

  // A sourcing always targets assets (portfolio-wide sourcing was removed on 2026-08-16); the
  // Assets field already says so when none is picked.
  const canLaunch = Boolean(selectedPortfolioId) && subtool === 'asset';

  // ── Saving and loading the launch parameters ─────────────────────────────────────────────────
  //
  // The user's preset already exists as ONE doctrine document on their own layer, carrying their
  // shortlist instructions and their rubric (lib/leasing/sourcingPreset.ts). The parameters are a
  // third section of that same document: no table, no column, no endpoint.
  //
  // SAVE READS BEFORE IT WRITES. The document is shared with the agent's SAVE_SOURCING_PRESET,
  // which stores instructions and a rubric; serialising from the form alone would silently delete
  // both. So: fetch, keep the halves this panel does not own, replace only the parameters.
  const [presetBusy, setPresetBusy] = useState<'idle' | 'loading'>('idle');
  // WHICH SET IS IN THE FORM. Without this the trigger always read "Load", so picking a set gave no
  // sign anything had happened - and when the set only differs from the form by one field, the user
  // sees literally nothing change. Reported 2026-08-13 as "it doesn't work and comes back to Load".
  const [loadedSetName, setLoadedSetName] = useState<string | null>(null);
  // Same open/dismiss contract as the panel's other pickers.
  const [presetMenuOpen, setPresetMenuOpen] = useState(false);
  const presetMenuRef = useDismissOnOutside(presetMenuOpen, () => setPresetMenuOpen(false));

  const readPresetDocument = useCallback(async (): Promise<string> => {
    const response = await fetch(`/api/doctrine?key=${encodeURIComponent(SOURCING_PRESET_DOCTRINE_KEY)}`);
    if (!response.ok) return '';
    const payload = await response.json().catch(() => null);
    return typeof payload?.document?.content === 'string' ? payload.document.content : '';
  }, []);

  /** Exactly what the form holds. Never a property, asset or portfolio id: a preset is reusable. */
  const currentParams = useCallback((): SourcingPresetParams => ({
    countryCodes,
    acceptedCnccActivities: cnccActivities,
    budgetCredits,
    minimumNetworkSize,
    minimumFitScore,
    surfaceMinSqm,
    surfaceMaxSqm,
    acceptedFormats: formats,
    excludeRecentlyScored,
    excludeOnsiteTenants,
    requireKnownWebsite,
    excludeOffScope,
  }), [
    countryCodes, cnccActivities, budgetCredits, minimumNetworkSize,
    minimumFitScore, surfaceMinSqm, surfaceMaxSqm, formats, excludeRecentlyScored, excludeOnsiteTenants,
    requireKnownWebsite,
    excludeOffScope,
  ]);

  /**
   * Apply a saved set to the form. ONE path, used by the menu and by the agent alike: each field
   * applies only when the preset carries it, through the form's own setters, so a set that says
   * nothing about a field leaves the user's value alone rather than zeroing it.
   */
  const applyParams = useCallback((saved: SourcingPresetParams) => {
    if (Object.prototype.hasOwnProperty.call(saved, 'countryCodes')) {
      setCountryCodes(saved.countryCodes ?? []);
      setCountryMode('MANUAL');
    } else {
      setCountryCodes(() => []);
      setCountryMode('ASSETS');
    }
    if (saved.acceptedCnccActivities) setCnccActivities(saved.acceptedCnccActivities);
    if (typeof saved.budgetCredits === 'number') setBrandCount(brandsFromBudget(saved.budgetCredits));
    if (typeof saved.minimumNetworkSize === 'number') setMinimumNetworkSize(saved.minimumNetworkSize);
    if (typeof saved.minimumFitScore === 'number') setMinimumFitScore(saved.minimumFitScore);
    if (typeof saved.surfaceMinSqm === 'number') setSurfaceMinSqm(saved.surfaceMinSqm);
    if (typeof saved.surfaceMaxSqm === 'number') setSurfaceMaxSqm(saved.surfaceMaxSqm);
    if (saved.acceptedFormats) setFormats(saved.acceptedFormats);
    if (typeof saved.excludeRecentlyScored === 'boolean') setExcludeRecentlyScored(saved.excludeRecentlyScored);
    if (typeof saved.excludeOnsiteTenants === 'boolean') setExcludeOnsiteTenants(saved.excludeOnsiteTenants);
    if (typeof saved.requireKnownWebsite === 'boolean') setRequireKnownWebsite(saved.requireKnownWebsite);
    if (typeof saved.excludeOffScope === 'boolean') setExcludeOffScope(saved.excludeOffScope);
  }, []);

  // THE AGENT NEEDS TO SEE THIS FORM, and it lives in component-local useState. Rather than lift
  // twelve values into the store (a refactor of a surface that is load-bearing for autosave and the
  // launch payload), mirror the ONE assembled object the save already builds. That is all a command
  // needs to read, and `sourcePendingParams` is the matching channel for it to write.
  useEffect(() => {
    kernel.current?.setState({ sourceDraftParams: currentParams() });
  }, [kernel, currentParams]);

  // A command asked for a preset to be applied: same setters as the menu, then clear the channel so
  // it cannot re-apply on every later render.
  const pendingParams = useKernelStore((state) => state.sourcePendingParams);
  useEffect(() => {
    if (!pendingParams) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the value arrives from outside React (a command writes the store slot), and applying it means driving the twelve local setters, so there is no render-time place to consume it short of lifting the whole form into the store
    applyParams(pendingParams);
    kernel.current?.setState({ sourcePendingParams: null });
  }, [pendingParams, applyParams, kernel]);

  const [savedSets, setSavedSets] = useState<SourcingPresetParamsSet[]>([]);

  const handleLoadParams = useCallback(async (setName?: string) => {
    if (presetBusy !== 'idle') return;
    setPresetBusy('loading');
    try {
      const preset = parseSourcingPreset(await readPresetDocument());
      const sets = preset.paramSets ?? [];
      setSavedSets(sets);
      const chosen = setName ? sets.find((set) => set.name === setName) : sets[0];
      const saved = chosen?.params;
      // UNDEFINED means the preset has no opinion on parameters - a preset saved before this
      // existed, or one holding only a rubric. Leave the form exactly as the user left it rather
      // than zeroing every field.
      if (!saved) {
        toast.message(lang === 'fr' ? 'Aucun parametre enregistre' : 'No saved parameters yet');
        return;
      }
      // Each field applies only when the preset carries it, through the form's own setters, so
      // autosave and the agent's view of the draft stay consistent with what is on screen.
      applyParams(saved);
      // Filling the form is the whole action. Launching stays a separate, deliberate click: one
      // stray launch spends credits according to analysis cost.
      setLoadedSetName(chosen?.name ?? null);
      toast.success(
        chosen?.name
          ? (lang === 'fr' ? `« ${chosen.name} » charge` : `Loaded "${chosen.name}"`)
          : (lang === 'fr' ? 'Parametres charges' : 'Parameters loaded'),
      );
    } catch (error) {
      console.error('[SourceSetupPanel] loading the launch parameters failed', error);
      kernel.current?.handleError(lang === 'fr' ? 'Echec du chargement des parametres' : 'Could not load the parameters');
    } finally {
      setPresetBusy('idle');
    }
  }, [presetBusy, readPresetDocument, lang, kernel]);

  // Hand the form's brief to the kernel and keep the button's spinner. Everything else - cleaning
  // the grid, persisting the brief onto the entities the engine reads it from, the launch itself,
  // the campaign doctrine, the recents and the toasts - is one named kernel action
  // (launchSourceCampaignFromBrief), because this button used to carry its own copy of that
  // sequence and the copies drifted: the conversation fix landed in the kernel and never reached
  // the button, so a launch discarded the whole setup exchange.
  const handleLaunch = useCallback(async () => {
    const k = kernel.current;
    if (!k || launching || !selectedPortfolioId) return;

    // A SOURCING ALWAYS TARGETS ASSETS. Removing the "Whole portfolio" label alone would only hide
    // the path: an empty selection still reached the launch and produced a portfolio-wide run, which
    // is the shape being retired (see the Assets field above for the four defects it carried).
    // Refused here rather than by disabling the button, because a dead button explains nothing and
    // the user has to be told which choice is missing.
    if (selectedAssetIds.length === 0) {
      toast.error(
        lang === 'fr'
          ? 'Choisissez au moins un actif avant de lancer.'
          : 'Choose at least one asset before launching.',
      );
      return;
    }

    setLaunching(true);
    try {
      await k.launchSourceCampaignFromBrief({
        portfolioId: selectedPortfolioId,
        assetIds: selectedAssetIds,
        brandCount,
        budgetCredits,
        countryCodes,
        minimumNetworkSize,
        minimumFitScore,
        cnccActivities,
        surfaceMinSqm,
        surfaceMaxSqm,
        formats,
        excludeOnsiteTenants,
        excludeRecentlyScored,
        requireKnownWebsite,
        excludeOffScope,
        useLegacyData,
        instructions: instructionsByScope.asset,
        lang,
      });
    } finally {
      setLaunching(false);
    }
  }, [
    kernel,
    launching,
    selectedPortfolioId,
    selectedAssetIds,
    excludeOnsiteTenants,
    cnccActivities,
    surfaceMinSqm,
    surfaceMaxSqm,
    formats,
    minimumFitScore,
    instructionsByScope,
    countryCodes,
    minimumNetworkSize,
    budgetCredits,
    excludeRecentlyScored,
    requireKnownWebsite,
    excludeOffScope,
    useLegacyData,
    lang,
  ]);

  const handlePortfolioChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => handleSelect(event.target.value),
    [handleSelect]
  );
  const openDoctrine = useCallback(() => setDoctrineOpen(true), []);
  const closeDoctrine = useCallback(() => setDoctrineOpen(false), []);
  const handleInstructionsChange = useCallback(
    (next: string) => setInstructionsByScope((prev) => ({ ...prev, [subtool]: next })),
    [subtool]
  );
  // Greys out + blocks interaction for portfolio-dependent params until a
  // portfolio is selected.
  const fieldClass = 'mt-4' + (disabled ? ' pointer-events-none opacity-40' : '');

  // When a brand is focused in the editor (a row is expanded), the right bar
  // shows its context panel instead of the sourcing params, the main editor
  // (table) stays untouched. Closing happens by collapsing the row.
  // A live run keeps its stop control on top whatever the bar shows (owner, 2026-09-28).
  const stopControl = sourceSelectedCampaignId && openCampaignActive
    ? <div className="w-full px-3"><StopRunButton campaignId={sourceSelectedCampaignId} lang={lang} /></div>
    : null;
  if (sourceFocusedFitId) {
    return <>{stopControl}<SourceBrandPanel fitId={sourceFocusedFitId} lang={lang} /></>;
  }

  // When fit rows are selected (and none is expanded) inside an open campaign, the right
  // bar offers a bulk "Enrich all selected" instead of the setup form.
  if (sourceSelectedFitIds.length > 0 && sourceSelectedCampaignId) {
    return <>{stopControl}<SourceBulkEnrichPanel fitIds={sourceSelectedFitIds} lang={lang} /></>;
  }

  // Inside an OPEN shortlist (a campaign is selected, nothing row-selected or expanded) you must
  // NOT be able to launch another sourcing, the right bar is the shortlist's technical bar (enrich
  // the brands), never the launch form. The launch form only appears when creating a new campaign
  // or on the dashboard (below). What the open run covered is read off the run itself.
  if (sourceSelectedCampaignId && !sourceCreatingCampaign && openCampaignScope !== 'portfolio') {
    return (
      <SourceShortlistPanel
        campaignId={sourceSelectedCampaignId}
        scope={openCampaignScope}
        lang={lang}
        stillRunning={openCampaignActive}
      />
    );
  }

  // On the campaign dashboard the right bar has NOTHING: the create action moved to the glowing
  // button at the bottom of the canvas (CampaignDashboard, 2026-08-23) and the hosts already
  // stand the panel down on the shared predicate (sourceDashboardOwnsCanvas). This guard only
  // covers the render that can slip through while those hosts re-evaluate.
  const onDashboard = sourceHeaderIsDashboard && Boolean(selectedPortfolioId);
  if (!sourceCreatingCampaign && onDashboard) {
    return null;
  }

  return (
    <div className="custom-scrollbar flex h-full w-full flex-col overflow-y-auto px-3 py-3">
      {/* Back-to-dashboard is the header's up-arrow, the single such affordance, no inline link. */}
      {/* Portfolio (always active) */}
      <label className="mb-1 block text-sm font-medium text-white">Portfolio</label>
      <select
        value={selectedPortfolioId ?? ''}
        onChange={handlePortfolioChange}
        style={nativeSelectStyle}
        className="text-sm"
      >
        {!selectedPortfolioId && (
          <option value="" disabled>
            {lang === 'fr' ? 'Sélectionner un portfolio' : 'Select portfolio'}
          </option>
        )}
        {portfolios.map((portfolio) => (
          <option key={portfolio.id} value={portfolio.id}>
            {portfolio.name}
          </option>
        ))}
      </select>

      {/* SCOPE. Pick the assets this sourcing covers. AT LEAST ONE, ALWAYS.
          Portfolio-wide sourcing was removed on 2026-08-16, the same way unit sourcing went on
          2026-08-02, and for the same reason: it was a second-class path that produced a different
          product on the same screen. It wrote into the portfolio's shared reference shortlist, so a
          brand new campaign opened onto 10,254 accumulated rows carrying OLD scores, overwritten one
          by one as the pass ran; its campaigns could not be deleted (the delete route answers 404
          for anything that is not asset-level); and it launched without a conversation, leaving an
          empty chat beside a running campaign. Four defects, one cause. */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">
          {lang === 'fr' ? 'Actifs' : 'Assets'}
        </label>
        <IdMultiSelect
          options={assetOptions}
          value={selectedAssetIds}
          onChange={setSelectedAssetIds}
          emptyLabel={lang === 'fr' ? 'Choisir un actif' : 'Choose an asset'}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {selectedAssetIds.length === 0
            ? lang === 'fr'
              ? 'Choisissez au moins un actif : un sourcing porte toujours sur des actifs.'
              : 'Choose at least one asset: a sourcing always targets assets.'
            : selectedAssetIds.length === 1
              ? lang === 'fr'
                ? 'Le sourcing couvre cet actif.'
                : 'The sourcing covers this asset.'
              : lang === 'fr'
                ? `Un seul sourcing couvre ces ${selectedAssetIds.length} actifs, analysés l'un après l'autre, budget partagé entre eux.`
                : `One sourcing covers these ${selectedAssetIds.length} assets, analysed one after another, with the budget shared between them.`}
        </p>
      </div>

      {/* Target countries */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">{lang === 'fr' ? 'Pays ciblés' : 'Target countries'}</label>
        <CountryMultiSelect
          mode={countryMode}
          value={countryCodes}
          onChange={handleCountryCodesChange}
          onModeChange={handleCountryModeChange}
          effectiveCountryCodes={effectiveCountryCodes}
          lang={lang}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {lang === 'fr'
            ? 'Limiter les marques à des pays spécifiques.'
            : 'Limit candidate brands to specific countries.'}
        </p>
      </div>

      {subtool === 'asset' ? (
        <div className={fieldClass}>
          <label className="mb-1 block text-sm font-medium text-white">
            {lang === 'fr' ? 'Catégories CNCC' : 'CNCC categories'}
          </label>
          <CnccMultiSelect value={cnccActivities} onChange={setCnccActivities} lang={lang} />
          <p className="mt-1 text-xs text-[#4D4D4D]">
            {lang === 'fr'
              ? 'Cibler des secteurs CNCC précis (ex. Équipement de la personne).'
              : 'Focus the campaign on specific CNCC sectors.'}
          </p>
        </div>
      ) : null}

      {subtool === 'asset' ? (
        <div className={fieldClass}>
          <RangeControl
            label={lang === 'fr' ? 'Surface minimale (m²)' : 'Minimum surface (m²)'}
            min={0}
            max={5000}
            step={10}
            value={surfaceMinSqm}
            onValueChange={setSurfaceMinSqm}
            description={lang === 'fr' ? '0 = pas de minimum. Les marques sans donnée surface sont conservées.' : '0 = no minimum. Brands with unknown surface are kept.'}
          />
        </div>
      ) : null}

      {subtool === 'asset' ? (
        <div className={fieldClass}>
          <RangeControl
            label={lang === 'fr' ? 'Surface maximale (m²)' : 'Maximum surface (m²)'}
            min={0}
            max={10000}
            step={10}
            value={surfaceMaxSqm}
            onValueChange={setSurfaceMaxSqm}
            description={lang === 'fr' ? '0 = pas de maximum. Exclut les enseignes dont la surface type ne peut pas rentrer.' : '0 = no maximum. Excludes brands whose typical format cannot fit.'}
          />
        </div>
      ) : null}

      {subtool === 'asset' ? (
        <div className={fieldClass}>
          <label className="mb-1 block text-sm font-medium text-white">{lang === 'fr' ? 'Format' : 'Format'}</label>
          <IdMultiSelect options={formatOptions(lang)} value={formats} onChange={setFormats} emptyLabel={lang === 'fr' ? 'Tous les formats' : 'Any format'} />
          <p className="mt-1 text-xs text-[#4D4D4D]">
            {lang === 'fr'
              ? 'Une enseigne qui ouvre surtout d’autres formats (des kiosques, des corners) est plafonnée sur la surface et le format.'
              : 'A brand that mostly opens other formats (kiosks, corners) is capped on surface and format.'}
          </p>
        </div>
      ) : null}

      {/* Budget */}
      <div className={fieldClass}>
        <RangeControl
          label={lang === 'fr' ? 'Marques à analyser' : 'Brands to analyse'}
          min={1}
          max={brandMax}
          step={1}
          value={brandCount}
          onValueChange={setBrandCount}
          description={
            lang === 'fr'
              ? `≈ ${budgetCredits} crédits pour ${brandCount} marque${brandCount > 1 ? 's' : ''}${selectedAssetIds.length > 1 ? ` par actif (${selectedAssetIds.length} actifs)` : ''} : choix, recherche et analyse, facturés au coût réel.`
              : `≈ ${budgetCredits} credits for ${brandCount} brand${brandCount > 1 ? 's' : ''}${selectedAssetIds.length > 1 ? ` per asset (${selectedAssetIds.length} assets)` : ''}: pick, research and analysis, billed at real cost.`
          }
        />
        {subtool === 'asset' && selectedAssetId ? (
          <div className="mt-1 text-[11px] text-white/45">
            {eligibleCountLoading
              ? lang === 'fr'
                ? 'Calcul des marques éligibles…'
                : 'Counting eligible brands…'
              : eligibleCount != null
                ? lang === 'fr'
                  ? `≈ ${eligibleCount} marque${eligibleCount > 1 ? 's' : ''} éligible${eligibleCount > 1 ? 's' : ''} avec ces filtres.`
                  : `≈ ${eligibleCount} eligible brand${eligibleCount > 1 ? 's' : ''} with these filters.`
                : ''}
          </div>
        ) : null}
      </div>

      {/* Minimum network size */}
      <div className={fieldClass}>
        <RangeControl
          label={lang === 'fr' ? 'Taille de réseau minimale' : 'Minimum network size'}
          min={1}
          max={500}
          step={1}
          value={minimumNetworkSize}
          onValueChange={setMinimumNetworkSize}
          description={
            lang === 'fr'
              ? 'Nombre minimal de points de vente déclarés/estimés.'
              : 'Minimum declared/estimated store count.'
          }
        />
      </div>

      {/* Exclude recently scored */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">
          {lang === 'fr' ? 'Exclure les récemment évalués' : 'Exclude recently scored'}
        </label>
        <Switch
          checked={excludeRecentlyScored}
          onCheckedChange={setExcludeRecentlyScored}
          labelLeft={lang === 'fr' ? 'Non' : 'Off'}
          labelRight={lang === 'fr' ? 'Oui' : 'On'}
          disabled={disabled}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {lang === 'fr'
            ? 'Ignorer les marques évaluées au cours des 6 derniers mois.'
            : 'Skip brands scored in the last 6 months.'}
        </p>
      </div>

      {/* Exclude on-site tenants */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">
          {lang === 'fr' ? 'Exclure les enseignes déjà présentes' : 'Exclude on-site tenants'}
        </label>
        <Switch
          checked={excludeOnsiteTenants}
          onCheckedChange={setExcludeOnsiteTenants}
          labelLeft={lang === 'fr' ? 'Non' : 'Off'}
          labelRight={lang === 'fr' ? 'Oui' : 'On'}
          disabled={disabled}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {lang === 'fr'
            ? 'Ne pas re-sourcer les marques déjà présentes dans le portfolio.'
            : 'Skip brands already present in the portfolio.'}
        </p>
      </div>

      {/* Require known website */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">
          {lang === 'fr' ? 'Site web connu requis' : 'Require known website'}
        </label>
        <Switch
          checked={requireKnownWebsite}
          onCheckedChange={setRequireKnownWebsite}
          labelLeft={lang === 'fr' ? 'Non' : 'Off'}
          labelRight={lang === 'fr' ? 'Oui' : 'On'}
          disabled={disabled}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {lang === 'fr'
            ? 'Ne garder que les marques avec un site web vérifié.'
            : 'Only keep brands with a verified website.'}
        </p>
      </div>

      {/* Off scope: unlisted or scored lower (owner, 2026-10-05). */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">
          {lang === 'fr' ? 'Masquer les enseignes hors cible' : 'Hide off-scope brands'}
        </label>
        <Switch
          checked={excludeOffScope}
          onCheckedChange={setExcludeOffScope}
          labelLeft={lang === 'fr' ? 'Non' : 'Off'}
          labelRight={lang === 'fr' ? 'Oui' : 'On'}
          disabled={disabled}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {lang === 'fr'
            ? 'Une enseigne dont le format habituel sort de la surface visée n’est pas analysée, et une enseigne qui manque clairement le brief est écartée. Désactivé : elle reste dans la liste avec une note plus basse.'
            : 'A brand whose usual format falls outside the surface sought is not analysed, and one that clearly misses the brief is set aside. Off: it stays listed with a lower score.'}
        </p>
      </div>

      {/* Every picked brand is researched before the run's one scoring (lib/leasing/researchStage.ts),
          billed on its metered cost within the budget. This switch only decides whether fields
          checked in the last 30 days are kept or re-checked too. */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">
          {lang === 'fr' ? 'Données existantes' : 'Legacy data'}
        </label>
        <Switch
          checked={useLegacyData}
          onCheckedChange={toggleUseLegacyData}
          labelLeft={lang === 'fr' ? 'Non' : 'Off'}
          labelRight={lang === 'fr' ? 'Oui' : 'On'}
          disabled={disabled}
        />
        <p className="mt-1 text-xs text-[#4D4D4D]">
          {useLegacyData
            ? (lang === 'fr' ? "Chaque marque choisie est re-vérifiée champ par champ avant sa notation ; les champs vérifiés il y a moins de 30 jours sont gardés. Facturé au coût réel, dans le budget." : 'Every picked brand is re-checked field by field before it is scored; fields checked in the last 30 days are kept. Billed at real cost, within the budget.')
            : (lang === 'fr' ? 'Tout est recherché de zéro, même les champs vérifiés récemment, et remplace les données en base.' : 'Everything is researched from scratch, recently checked fields included, and replaces the stored data.')}
        </p>
      </div>

      {/* Doctrine, scoring rubric (.csv) + agent instructions (.md) */}
      <div className={fieldClass}>
        <label className="mb-1 block text-sm font-medium text-white">Doctrine</label>
        <DoctrineEntryCard onOpen={openDoctrine} lang={lang} />
      </div>

      <SourceDoctrineModal
        open={doctrineOpen}
        onClose={closeDoctrine}
        scope={subtool}
        lang={lang}
        rubric={DEFAULT_PROPERTY_SCORING_RUBRIC}
        instructions={instructionsByScope[subtool]}
        onInstructionsChange={handleInstructionsChange}
      />

      {/* Launch, matches Studio's GenerateButton (gray) */}
      {/* MY PARAMETERS: the same label-over-control row every other field in this panel uses, and
          the panel's own `selectStyle`, not a second dialect invented here. The first version was
          hand-rolled with its own heights, radii and hexes; it wrapped onto two lines and matched
          nothing around it.
          Loading fills the form and stops - the launch below stays a deliberate, separate act,
          because one stray click spends credits according to analysis cost. */}
      <div className="mt-4">
        <div className="mb-1 text-xs text-white/40">
          {lang === 'fr' ? 'Mes parametres' : 'My parameters'}
        </div>
        <div className="flex items-center">
          {/* A picker, like every other choice in this panel: same selectStyle trigger, same
              ChevronDown, same dismiss-on-outside, same menu surface as the four multi-selects
              above. Built from those primitives rather than a fifth component, because it picks
              ONE saved set where they pick many values. */}
          <div className="relative flex-1" ref={presetMenuRef}>
            <button
              type="button"
              onClick={() => {
                setPresetMenuOpen((open) => {
                  // Read the names on open: cheap, and it means the menu shows what the agent saved
                  // in this same session without a reload.
                  if (!open) void readPresetDocument().then((content) => setSavedSets(parseSourcingPreset(content).paramSets ?? []));
                  return !open;
                });
              }}
              disabled={presetBusy !== 'idle'}
              style={selectStyle}
              className="flex items-center justify-between gap-2 text-left text-sm disabled:opacity-50"
            >
              <span className="truncate text-[#cfcfcf]">
                {presetBusy === 'loading'
                  ? (lang === 'fr' ? 'Chargement…' : 'Loading…')
                  : loadedSetName ?? (lang === 'fr' ? 'Charger' : 'Load')}
              </span>
              <ChevronDown
                size={16}
                className={'shrink-0 text-[#909090] transition-transform ' + (presetMenuOpen ? 'rotate-180' : '')}
              />
            </button>
            {presetMenuOpen ? (
              <div className="absolute left-0 z-20 mt-1 w-max min-w-full whitespace-nowrap overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#353535] py-1 shadow-[0_16px_44px_rgba(0,0,0,0.45)]">
                {savedSets.length === 0 ? (
                  // Nothing saved: say so rather than offer a name that resolves to nothing. Only
                  // the agent writes sets now, so the empty state has to point at it.
                  <div className="px-2 py-1.5 text-sm text-[#6E6E6E]">
                    {lang === 'fr' ? 'Aucun jeu enregistre' : 'No saved sets'}
                  </div>
                ) : (
                  savedSets.map((set) => (
                    <button
                      key={set.name}
                      type="button"
                      onClick={() => { setPresetMenuOpen(false); void handleLoadParams(set.name); }}
                      className="w-full px-2 py-1.5 text-left text-sm text-[#cfcfcf] hover:bg-white/5"
                    >
                      {set.name}
                    </button>
                  ))
                )}
              </div>
            ) : null}
          </div>
        </div>
        <p className="mt-1 text-[11px] leading-snug text-[#6E6E6E]">
          {lang === 'fr'
            ? 'Demandez a l\'agent d\'enregistrer et de nommer un jeu. Charger remplit ce formulaire, sans lancer.'
            : 'Ask the agent to save and name a set. Loading fills this form; it does not launch.'}
        </p>
      </div>
      <button
        type="button"
        onClick={handleLaunch}
        disabled={!canLaunch}
        className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-white font-medium text-black transition-colors hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-50 mt-3 h-[36px] w-full px-3 text-sm"
      >
        {launching ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
            {lang === 'fr' ? 'Lancement…' : 'Launching…'}
          </>
        ) : (
          lang === 'fr' ? 'Lancer le sourcing' : 'Launch sourcing'
        )}
      </button>
    </div>
  );
};

SourceSetupPanel.displayName = 'SourceSetupPanel';
